import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTION_CONFIG } from '../config';
import SubscriptionPlans from './SubscriptionPlans';
import SubscriptionInvoice from './SubscriptionInvoice';
import OrderDetails from './OrderDetails';

const GET_MY_ORDERS = gql`
  query GetMyOrders {
    myOrders {
      id
      amount
      currency
      status
      razorpay_order_id
      razorpay_payment_id
      created_at
      package {
        id
        name
        price
        duration_days
      }
    }
  }
`;

const GET_MY_SUBSCRIPTIONS = gql`
  query GetMySubscriptions {
    mySubscriptions {
      id
      status
      starts_at
      expires_at
      created_at
      package {
        id
        name
        price
        currency
        duration_days
        max_staff_members
        max_contacts
        max_whatsapp_instances
        max_bulk_messages_per_day
      }
      order {
        id
        amount
        status
        razorpay_payment_id
      }
    }
  }
`;

const OrderManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('subscriptions'); // 'subscriptions' or 'orders'
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [showQueueConfirmationModal, setShowQueueConfirmationModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Filter state - default to current month/year
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [showAllData, setShowAllData] = useState(false); // Toggle to show all data or filtered

  const { data: ordersData, loading: ordersLoading, error: ordersError } = useQuery(GET_MY_ORDERS, {
    skip: activeTab !== 'orders',
    fetchPolicy: 'network-only', // Always fetch fresh data from server
    nextFetchPolicy: 'network-only' // Keep fetching fresh data on subsequent requests
  });

  const { data: subscriptionsData, loading: subscriptionsLoading, error: subscriptionsError } = useQuery(GET_MY_SUBSCRIPTIONS, {
    skip: activeTab !== 'subscriptions',
    fetchPolicy: 'network-only', // Always fetch fresh data from server
    nextFetchPolicy: 'network-only' // Keep fetching fresh data on subsequent requests
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date;

    // Handle both timestamp (number) and date string formats
    if (typeof timestamp === 'string' && !isNaN(timestamp)) {
      // If it's a numeric string (timestamp), convert to number first
      date = new Date(parseInt(timestamp));
    } else if (typeof timestamp === 'number') {
      // If it's already a number (timestamp)
      date = new Date(timestamp);
    } else {
      // If it's a date string
      date = new Date(timestamp);
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date format for timestamp:', timestamp);
      return 'Invalid Date';
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: 'check_circle' },
      expired: { bg: 'bg-red-100', text: 'text-red-800', icon: 'cancel' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', icon: 'block' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: 'check_circle' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: 'error' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: 'schedule' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: 'hourglass_empty' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-normal ${config.bg} ${config.text}`}>
        <span className="material-icons-round text-xs">{config.icon}</span>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 0;

    const now = new Date();
    let expiry;

    // Handle both timestamp (number) and date string formats
    if (typeof expiresAt === 'string' && !isNaN(expiresAt)) {
      // If it's a numeric string (timestamp), convert to number first
      expiry = new Date(parseInt(expiresAt));
    } else if (typeof expiresAt === 'number') {
      // If it's already a number (timestamp)
      expiry = new Date(expiresAt);
    } else {
      // If it's a date string
      expiry = new Date(expiresAt);
    }

    // Check if the date is valid
    if (isNaN(expiry.getTime())) {
      console.warn('Invalid date format for expiresAt:', expiresAt);
      return 0;
    }

    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays; // Can be negative for expired subscriptions
  };

  // Check if subscription is in grace period (configurable days after expiry)
  const isInGracePeriod = (expiresAt) => {
    const daysRemaining = getDaysRemaining(expiresAt);
    const gracePeriodDays = SUBSCRIPTION_CONFIG.GRACE_PERIOD_DAYS;
    return daysRemaining < 0 && daysRemaining >= -gracePeriodDays; // Expired but within grace period
  };

  // Get warning level based on days remaining
  const getWarningLevel = (daysRemaining) => {
    if (daysRemaining < 0) return 'expired';
    if (daysRemaining <= 1) return 'critical';
    if (daysRemaining <= 3) return 'urgent';
    if (daysRemaining <= 7) return 'warning';
    return 'normal';
  };

  // Function to handle plan initialization with email verification check
  const handleInitializePlan = () => {
    if (!user?.email_verified) {
      setShowEmailVerificationModal(true);
      return;
    }
    
    // Check if user has active or pending subscriptions
    const subscriptions = subscriptionsData?.mySubscriptions || [];
    const hasActiveOrPending = subscriptions.some(s => s.status === 'active' || s.status === 'pending');
    
    if (hasActiveOrPending) {
      // Show confirmation dialog first
      setShowQueueConfirmationModal(true);
    } else {
      // No active/pending subscriptions, open plans directly
      setShowSubscriptionModal(true);
    }
  };

  // Function to redirect to Profile
  const redirectToProfile = () => {
    setShowEmailVerificationModal(false);
    navigate('/dashboard', { state: { activeSection: 'profile' } });
  };

  // Filter data by month/year
  const filterByMonthYear = (items, dateField) => {
    if (showAllData || !items) return items;
    
    return items.filter(item => {
      const itemDate = new Date(item[dateField]);
      if (isNaN(itemDate.getTime())) return false;
      
      const itemMonth = itemDate.getMonth() + 1; // 1-12
      const itemYear = itemDate.getFullYear();
      
      return itemMonth === selectedMonth && itemYear === selectedYear;
    });
  };

  // Get filtered and paginated data
  const getFilteredOrders = () => {
    const filtered = filterByMonthYear(ordersData?.myOrders || [], 'created_at');
    return filtered;
  };

  const getFilteredSubscriptions = () => {
    const filtered = filterByMonthYear(subscriptionsData?.mySubscriptions || [], 'created_at');
    return filtered;
  };

  // Pagination logic
  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data) => {
    return Math.ceil(data.length / itemsPerPage);
  };

  // Reset to page 1 when filters change
  const handleFilterChange = (month, year, showAll) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setShowAllData(showAll);
    setCurrentPage(1);
  };

  // Reset to page 1 when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // Generate month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Generate year options (last 5 years)
  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Order History</h2>
            <p className="text-[9px] text-gray-400 font-normal uppercase tracking-wider mt-0.5">Subscriptions & Payment History</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
            <div className="flex bg-gray-50 border border-gray-100 p-1 rounded-xl">
              <button
                onClick={() => handleTabChange('subscriptions')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[9px] font-normal uppercase tracking-wider transition-all ${activeTab === 'subscriptions'
                  ? 'bg-white text-bulky-blue-600 shadow-sm'
                  : 'text-gray-400 hover:text-bulky-navy-800'
                  }`}
              >
                <span className="material-icons-round text-sm">subscriptions</span>
                Subscriptions
              </button>
              <button
                onClick={() => handleTabChange('orders')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[9px] font-normal uppercase tracking-wider transition-all ${activeTab === 'orders'
                  ? 'bg-white text-bulky-blue-600 shadow-sm'
                  : 'text-gray-400 hover:text-bulky-navy-800'
                  }`}
              >
                <span className="material-icons-round text-sm">receipt_long</span>
                Orders
              </button>
            </div>
            
            {/* View All Plans Button - Always visible */}
            <button
              onClick={handleInitializePlan}
              className="btn-primary !py-1.5 !px-4 !text-[9px] !rounded-lg !tracking-wider flex items-center gap-1"
            >
              <span className="material-icons-round text-xs">store</span>
              View All Plans
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="material-icons-round text-gray-400 text-sm">filter_list</span>
            <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider">Filter By:</span>
          </div>
          
          {/* Show All Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showAllData}
              onChange={(e) => handleFilterChange(selectedMonth, selectedYear, e.target.checked)}
              className="w-4 h-4 text-bulky-blue-600 border-gray-300 rounded focus:ring-bulky-blue-500"
            />
            <span className="text-[9px] font-normal text-gray-600 uppercase tracking-wider">Show All</span>
          </label>

          {/* Month Selector */}
          {!showAllData && (
            <>
              <select
                value={selectedMonth}
                onChange={(e) => handleFilterChange(parseInt(e.target.value), selectedYear, false)}
                className="px-3 py-1.5 text-[9px] font-normal uppercase tracking-wider border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-bulky-blue-500 focus:border-transparent"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>

              {/* Year Selector */}
              <select
                value={selectedYear}
                onChange={(e) => handleFilterChange(selectedMonth, parseInt(e.target.value), false)}
                className="px-3 py-1.5 text-[9px] font-normal uppercase tracking-wider border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-bulky-blue-500 focus:border-transparent"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </>
          )}

          {/* Results Count */}
          <div className="ml-auto flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg">
            <span className="material-icons-round text-gray-400 text-xs">list</span>
            <span className="text-[9px] font-normal text-gray-600 uppercase tracking-wider">
              {activeTab === 'subscriptions' 
                ? `${getFilteredSubscriptions().length} Subscription${getFilteredSubscriptions().length !== 1 ? 's' : ''}`
                : `${getFilteredOrders().length} Order${getFilteredOrders().length !== 1 ? 's' : ''}`
              }
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {activeTab === 'subscriptions' ? (
          /* Subscriptions Tab */
          <>
            {subscriptionsLoading ? (
              <div className="p-12 text-center">
                <div className="w-10 h-10 border-[3px] border-bulky-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider leading-none">Loading Subscriptions...</p>
              </div>
            ) : subscriptionsError ? (
              <div className="p-12 text-center text-red-500 uppercase tracking-wider text-[10px]">Error: {subscriptionsError.message}</div>
            ) : getFilteredSubscriptions().length > 0 ? (
              <>
                {/* Active Subscriptions */}
                {getPaginatedData(getFilteredSubscriptions().filter(s => s.status === 'active' || s.status === 'expired')).length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {getPaginatedData(getFilteredSubscriptions().filter(s => s.status === 'active' || s.status === 'expired'))
                      .map((subscription) => (
                  <div key={subscription.id} className="p-4 hover:bg-bulky-blue-50/20 transition-all duration-300">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h2 className="text-sm font-medium text-bulky-navy-800 tracking-tight uppercase leading-none">{subscription.package.name}</h2>
                          {getStatusBadge(subscription.status)}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Price</p>
                            <p className="text-[10px] font-normal text-bulky-navy-800 uppercase">₹{subscription.package.price}</p>
                          </div>
                          <div>
                            <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Duration</p>
                            <p className="text-[10px] font-normal text-bulky-navy-800 uppercase">{subscription.package.duration_days} Days</p>
                          </div>
                          <div>
                            <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Started</p>
                            <p className="text-[10px] font-normal text-bulky-navy-800 uppercase">{formatDate(subscription.starts_at)}</p>
                          </div>
                          <div>
                            <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">
                              {subscription.status === 'active' ? 'Expiry' : 'Expired'}
                            </p>
                            <p className="text-[10px] font-normal text-bulky-navy-800 uppercase">{formatDate(subscription.expires_at)}</p>
                          </div>
                        </div>

                        {subscription.status === 'active' && (
                          <>
                            {(() => {
                              const daysRemaining = getDaysRemaining(subscription.expires_at);
                              const warningLevel = getWarningLevel(daysRemaining);
                              
                              if (warningLevel === 'critical') {
                                return (
                                  <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 bg-red-50 rounded text-red-600 border border-red-200 animate-pulse">
                                    <span className="material-icons-round text-xs">warning</span>
                                    <p className="text-[8px] font-normal uppercase tracking-wider">
                                      Expires {daysRemaining === 0 ? 'Today' : 'Tomorrow'}!
                                    </p>
                                  </div>
                                );
                              } else if (warningLevel === 'urgent') {
                                return (
                                  <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 bg-orange-50 rounded text-orange-600 border border-orange-200">
                                    <span className="material-icons-round text-xs">schedule</span>
                                    <p className="text-[8px] font-normal uppercase tracking-wider">
                                      {daysRemaining}D Remaining - Renew Soon!
                                    </p>
                                  </div>
                                );
                              } else if (warningLevel === 'warning') {
                                return (
                                  <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 bg-yellow-50 rounded text-yellow-600 border border-yellow-200">
                                    <span className="material-icons-round text-xs">timer</span>
                                    <p className="text-[8px] font-normal uppercase tracking-wider">
                                      {daysRemaining}D Remaining
                                    </p>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 rounded text-emerald-600 border border-emerald-100">
                                    <span className="material-icons-round text-xs">check_circle</span>
                                    <p className="text-[8px] font-normal uppercase tracking-wider">
                                      {daysRemaining}D Remaining
                                    </p>
                                  </div>
                                );
                              }
                            })()}
                          </>
                        )}

                        {/* Grace Period Indicator */}
                        {subscription.status === 'expired' && isInGracePeriod(subscription.expires_at) && (
                          <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 bg-purple-50 rounded text-purple-600 border border-purple-200">
                            <span className="material-icons-round text-xs">hourglass_bottom</span>
                            <p className="text-[8px] font-normal uppercase tracking-wider">
                              Grace Period: {Math.abs(getDaysRemaining(subscription.expires_at))} of {SUBSCRIPTION_CONFIG.GRACE_PERIOD_DAYS} Days Used
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 lg:items-end">
                        <div className="text-[8px] font-normal text-gray-400 uppercase tracking-wider bg-gray-50 px-2 py-1 rounded">
                          # {subscription.order?.razorpay_payment_id || 'N/A'}
                        </div>
                        
                        <div className="flex gap-2">
                          {/* Renew Button - Show for active subscriptions expiring soon or expired/grace period */}
                          {(subscription.status === 'active' && getDaysRemaining(subscription.expires_at) <= 7) || 
                           subscription.status === 'expired' || 
                           isInGracePeriod(subscription.expires_at) ? (
                            <button
                              onClick={handleInitializePlan}
                              className="btn-primary !py-1.5 !px-4 !text-[9px] !rounded-lg !tracking-wider !bg-green-600 hover:!bg-green-700 flex items-center gap-1"
                            >
                              <span className="material-icons-round text-xs">autorenew</span>
                              Renew Now
                            </button>
                          ) : null}
                          
                          {/* Upgrade/View Plans Button - Show for active subscriptions not expiring soon */}
                          {subscription.status === 'active' && getDaysRemaining(subscription.expires_at) > 7 && (
                            <button
                              onClick={handleInitializePlan}
                              className="btn-primary !py-1.5 !px-4 !text-[9px] !rounded-lg !tracking-wider !bg-purple-600 hover:!bg-purple-700 flex items-center gap-1"
                            >
                              <span className="material-icons-round text-xs">upgrade</span>
                              View Plans
                            </button>
                          )}
                          
                          {/* View Details Button */}
                          <button
                            onClick={() => {
                              setSelectedSubscription(subscription);
                              setShowInvoiceModal(true);
                            }}
                            className={`btn-primary !py-1.5 !px-4 !text-[9px] !rounded-lg !tracking-wider ${
                              subscription.status !== 'active' ? 'bg-gray-600 hover:bg-gray-700' : ''
                            }`}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                  </div>
                )}

                {/* Pending/Upcoming Subscriptions */}
                {getFilteredSubscriptions().filter(s => s.status === 'pending').length > 0 && (
                  <div className="border-t-4 border-gray-100">
                    {/* Section Header */}
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 border-b border-purple-100">
                      <div className="flex items-center gap-2">
                        <span className="material-icons-round text-purple-600">schedule</span>
                        <h3 className="text-sm font-medium text-purple-900 tracking-tight uppercase">Upcoming Subscriptions</h3>
                        <span className="ml-auto bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-[9px] font-normal uppercase tracking-wider">
                          {getFilteredSubscriptions().filter(s => s.status === 'pending').length} Queued
                        </span>
                      </div>
                      <p className="text-[9px] text-purple-600 font-normal uppercase tracking-wider mt-1">
                        These subscriptions will activate when your current plan expires
                      </p>
                    </div>

                    {/* Pending Subscriptions List */}
                    <div className="divide-y divide-gray-100">
                      {getPaginatedData(getFilteredSubscriptions().filter(s => s.status === 'pending'))
                        .map((subscription) => (
                          <div key={subscription.id} className="p-4 hover:bg-purple-50/20 transition-all duration-300">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3">
                                  <h2 className="text-sm font-medium text-bulky-navy-800 tracking-tight uppercase leading-none">{subscription.package.name}</h2>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-normal bg-purple-100 text-purple-800">
                                    <span className="material-icons-round text-xs">schedule</span>
                                    Pending
                                  </span>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div>
                                    <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Price</p>
                                    <p className="text-[10px] font-normal text-bulky-navy-800 uppercase">₹{subscription.package.price}</p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Duration</p>
                                    <p className="text-[10px] font-normal text-bulky-navy-800 uppercase">{subscription.package.duration_days} Days</p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Activates On</p>
                                    <p className="text-[10px] font-normal text-purple-700 uppercase font-medium">{formatDate(subscription.starts_at)}</p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Will Expire</p>
                                    <p className="text-[10px] font-normal text-bulky-navy-800 uppercase">{formatDate(subscription.expires_at)}</p>
                                  </div>
                                </div>

                                {/* Days Until Activation */}
                                <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 bg-purple-50 rounded text-purple-600 border border-purple-200">
                                  <span className="material-icons-round text-xs">event</span>
                                  <p className="text-[8px] font-normal uppercase tracking-wider">
                                    Activates in {Math.max(0, getDaysRemaining(subscription.starts_at))} Days
                                  </p>
                                </div>
                              </div>

                              <div className="flex flex-col gap-2 lg:items-end">
                                <div className="text-[8px] font-normal text-gray-400 uppercase tracking-wider bg-gray-50 px-2 py-1 rounded">
                                  # {subscription.order?.razorpay_payment_id || 'N/A'}
                                </div>
                                
                                <button
                                  onClick={() => {
                                    setSelectedSubscription(subscription);
                                    setShowInvoiceModal(true);
                                  }}
                                  className="btn-primary !py-1.5 !px-4 !text-[9px] !rounded-lg !tracking-wider"
                                >
                                  View Details
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Pagination for Subscriptions */}
                {getTotalPages(getFilteredSubscriptions()) > 1 && (
                  <div className="border-t border-gray-100 p-4 flex items-center justify-between">
                    <div className="text-[9px] text-gray-500 uppercase tracking-wider">
                      Page {currentPage} of {getTotalPages(getFilteredSubscriptions())}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-[9px] font-normal uppercase tracking-wider border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: getTotalPages(getFilteredSubscriptions()) }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 text-[9px] font-normal rounded-lg transition-all ${
                            currentPage === page
                              ? 'bg-bulky-blue-600 text-white'
                              : 'border border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(getTotalPages(getFilteredSubscriptions()), prev + 1))}
                      disabled={currentPage === getTotalPages(getFilteredSubscriptions())}
                      className="px-3 py-1.5 text-[9px] font-normal uppercase tracking-wider border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              </>
            ) : (
              <div className="p-16 text-center flex flex-col items-center">
                <div className='flex flex-col items-center justify-center'>
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 border border-gray-100">
                  <span className="material-icons-round text-gray-300 text-3xl">hub</span>
                </div>
                <h2 className="font-medium">No Subscriptions</h2>
                <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider mb-6">You don't have any active subscriptions</p>
                </div>
                <button
                  onClick={handleInitializePlan}
                  className="btn-primary py-2 px-6"
                >
                  <span className="material-icons-round text-sm">shopping_cart</span>
                  Buy a Plan
                </button>
              </div>
            )}
          </>
        ) : (
          /* Orders Tab */
          <>
            {ordersLoading ? (
              <div className="p-12 text-center">
                <div className="w-10 h-10 border-[3px] border-bulky-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider leading-none">Loading Orders...</p>
              </div>
            ) : ordersError ? (
              <div className="p-12 text-center text-red-500 uppercase tracking-wider text-[10px]">Error: {ordersError.message}</div>
            ) : getFilteredOrders().length > 0 ? (
              <>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="px-4 py-3 text-[9px] font-normal text-gray-400 uppercase tracking-[0.2em]">Order ID</th>
                      <th className="px-4 py-3 text-[9px] font-normal text-gray-400 uppercase tracking-[0.2em]">Package</th>
                      <th className="px-4 py-3 text-[9px] font-normal text-gray-400 uppercase tracking-[0.2em]">Amount</th>
                      <th className="px-4 py-3 text-[9px] font-normal text-gray-400 uppercase tracking-[0.2em]">Status</th>
                      <th className="px-4 py-3 text-[9px] font-normal text-gray-400 uppercase tracking-[0.2em]">Date</th>
                      <th className="px-4 py-3 text-[9px] font-normal text-gray-400 uppercase tracking-[0.2em] text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-[10px] font-normal text-gray-600">
                    {getPaginatedData(getFilteredOrders()).map((order) => (
                      <tr key={order.id} className="hover:bg-bulky-blue-50/20 transition-all duration-200">
                        <td className="px-4 py-2.5 whitespace-nowrap font-normal text-bulky-navy-800">
                          #{order.id.substring(0, 8)}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <div className="font-normal text-bulky-navy-800 uppercase tracking-tight">{order.package.name}</div>
                          <div className="text-[8.5px] text-gray-400 uppercase tracking-wider">{order.package.duration_days} Days</div>
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap font-normal text-bulky-navy-800 uppercase tracking-tighter text-xs">
                          ₹{order.amount}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap font-normal uppercase text-gray-400 tracking-tight">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-right">
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderModal(true);
                            }}
                            className="w-10 h-10 bg-bulky-navy-800 hover:bg-bulky-blue-600 text-white rounded-xl transition-all flex items-center justify-center ml-auto shadow-sm active:scale-90"
                            title="View Details"
                          >
                            <span className="material-icons-round text-lg">visibility</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination for Orders */}
              {getTotalPages(getFilteredOrders()) > 1 && (
                <div className="border-t border-gray-100 p-4 flex items-center justify-between">
                  <div className="text-[9px] text-gray-500 uppercase tracking-wider">
                    Page {currentPage} of {getTotalPages(getFilteredOrders())}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-[9px] font-normal uppercase tracking-wider border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: getTotalPages(getFilteredOrders()) }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 text-[9px] font-normal rounded-lg transition-all ${
                            currentPage === page
                              ? 'bg-bulky-blue-600 text-white'
                              : 'border border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(getTotalPages(getFilteredOrders()), prev + 1))}
                      disabled={currentPage === getTotalPages(getFilteredOrders())}
                      className="px-3 py-1.5 text-[9px] font-normal uppercase tracking-wider border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              </>
            ) : (
              <div className="p-16 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100">
                  <span className="material-icons-round text-gray-300 text-3xl">receipt_long</span>
                </div>
                <h2 className="font-medium">No Orders</h2>
                <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider">You haven't made any purchases yet</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Queue Confirmation Modal */}
      {showQueueConfirmationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header - Fixed */}
            <div className="bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-900 p-6 text-white flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <span className="material-icons-round text-2xl">info</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-tight">Subscription Queue Information</h2>
                  <p className="text-bulky-navy-200 text-sm mt-1">Your current subscription status</p>
                </div>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Current Active Subscription */}
              {(() => {
                const activeSubscription = subscriptionsData?.mySubscriptions?.find(s => s.status === 'active');
                const pendingSubscriptions = subscriptionsData?.mySubscriptions?.filter(s => s.status === 'pending') || [];
                
                return (
                  <>
                    {activeSubscription && (
                      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="material-icons-round text-green-600">check_circle</span>
                          <h3 className="text-sm font-semibold text-green-900 uppercase tracking-wider">Current Active Plan</h3>
                        </div>
                        <div className="bg-white rounded-lg p-4 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600 uppercase tracking-wider">Plan Name</span>
                            <span className="text-sm font-semibold text-gray-900">{activeSubscription.package.name}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600 uppercase tracking-wider">Expires On</span>
                            <span className="text-sm font-semibold text-gray-900">{formatDate(activeSubscription.expires_at)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600 uppercase tracking-wider">Days Remaining</span>
                            <span className="text-sm font-semibold text-green-600">{getDaysRemaining(activeSubscription.expires_at)} Days</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pending Subscriptions */}
                    {pendingSubscriptions.length > 0 && (
                      <div className="bg-bulky-navy-50 border-2 border-bulky-navy-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="material-icons-round text-bulky-navy-600">schedule</span>
                          <h3 className="text-sm font-semibold text-bulky-navy-900 uppercase tracking-wider">
                            Queued Plans ({pendingSubscriptions.length})
                          </h3>
                        </div>
                        <div className="space-y-3">
                          {pendingSubscriptions.map((sub, index) => (
                            <div key={sub.id} className="bg-white rounded-lg p-4 space-y-2">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="bg-bulky-navy-100 text-bulky-navy-700 px-2 py-0.5 rounded text-xs font-semibold uppercase">
                                  Queue #{index + 1}
                                </span>
                                <span className="text-xs text-gray-500">→</span>
                                <span className="text-sm font-semibold text-gray-900">{sub.package.name}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-600 uppercase tracking-wider">Starts</span>
                                <span className="font-medium text-gray-900">{formatDate(sub.starts_at)}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-600 uppercase tracking-wider">Expires</span>
                                <span className="font-medium text-gray-900">{formatDate(sub.expires_at)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Information Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex gap-3">
                        <span className="material-icons-round text-blue-600 text-xl flex-shrink-0">lightbulb</span>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-blue-900 mb-2">What happens when you purchase a new plan?</h4>
                          <ul className="space-y-2 text-xs text-blue-800">
                            {activeSubscription && pendingSubscriptions.length === 0 && (
                              <li className="flex items-start gap-2">
                                <span className="material-icons-round text-xs mt-0.5">arrow_right</span>
                                <span>Your new plan will be <strong>queued</strong> and start automatically on <strong>{formatDate(activeSubscription.expires_at)}</strong> when your current plan expires.</span>
                              </li>
                            )}
                            {activeSubscription && pendingSubscriptions.length > 0 && (
                              <li className="flex items-start gap-2">
                                <span className="material-icons-round text-xs mt-0.5">arrow_right</span>
                                <span>Your new plan will be queued as <strong>Queue #{pendingSubscriptions.length + 1}</strong> and start on <strong>{formatDate(pendingSubscriptions[pendingSubscriptions.length - 1].expires_at)}</strong> after your last queued plan expires.</span>
                              </li>
                            )}
                            <li className="flex items-start gap-2">
                              <span className="material-icons-round text-xs mt-0.5">arrow_right</span>
                              <span>All queued plans activate <strong>automatically</strong> - no action needed from you.</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="material-icons-round text-xs mt-0.5">arrow_right</span>
                              <span>You can purchase multiple plans in advance and they will chain perfectly.</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Footer - Fixed */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end flex-shrink-0 border-t border-gray-200">
              <button
                onClick={() => setShowQueueConfirmationModal(false)}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors uppercase tracking-wider"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowQueueConfirmationModal(false);
                  setShowSubscriptionModal(true);
                }}
                className="px-6 py-2.5 text-sm font-medium text-white bg-bulky-navy-600 rounded-lg hover:bg-bulky-navy-700 transition-all uppercase tracking-wider flex items-center gap-2"
              >
                <span className="material-icons-round text-sm">shopping_cart</span>
                Continue to Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Plans Modal */}
      <SubscriptionPlans
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSuccess={(subscription) => {
          setShowSubscriptionModal(false);
          // Refresh the page to show new subscription
          window.location.reload();
        }}
      />

      {/* Subscription Invoice Modal */}
      <SubscriptionInvoice
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedSubscription(null);
        }}
        subscription={selectedSubscription}
      />

      {/* Order Details Modal */}
      <OrderDetails
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
      />

      {/* Email Verification Required Modal */}
      {showEmailVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <span className="material-icons-round text-2xl">email</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-tight">Email Verification Required</h2>
                  <p className="text-red-100 text-sm mt-1">Please verify your email to continue</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="material-icons-round text-red-500 text-2xl">mark_email_unread</span>
              </div>
              <p className="text-gray-600 mb-6">
                You need to verify your email address before you can access subscription plans. 
                Please go to your Profile to verify your email.
              </p>
              
              {/* Only one button - Verify Email */}
              <button
                onClick={redirectToProfile}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <span className="material-icons-round text-sm">verified_user</span>
                Verify Email Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;